import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '../../../services/auth.service';
import { SchedulesService, Schedule, ExcludedDay, TimeSlot as ApiTimeSlot } from '../../../services/schedules.service';
import { GroupsService } from '../../../services/groups.service';

interface TimeSlot {
  start: string;
  end: string;
  capacity?: number;
}

interface WeekDay {
  value: string;
  label: string;
  selected: boolean;
}

interface PreviewDay {
  fullDate: Date;
  dayName: string;
  dayNumber: number;
  monthName: string;
  available: boolean;
  reason?: string;
  timeSlots: TimeSlot[];
}

@Component({
  selector: 'app-schedule',
  templateUrl: './schedule.component.html',
  styleUrls: ['./schedule.component.css']
})
export class ScheduleComponent implements OnInit {

  agendas: any[] = [];
  groups: Array<{ id: number; name: string }> = [];
  loading = false;
  error: string | null = null;

  // Estados do formulário
  isCreating = false;
  isEditing = false;
  currentStep = 1;
  isSaving = false;
  editingAgendaId: number | null = null;

  // Dados do formulário
  formData = {
    name: '',
    description: '',
    groupId: null as number | null,
    canExternalLink: false,
    allDays: true,
    excludeHolidays: false,
    excludeSpecificDates: false,
    excludedDates: [] as string[],
    commercialHours: true,
    includePresenceStatus: false,
    intervalDuration: '30',
    attendancesPerSlot: 1,
    timeSlots: [] as TimeSlot[]
  };

  // Dias da semana
  weekDays: WeekDay[] = [
    { value: 'sunday', label: 'Domingo', selected: false },
    { value: 'monday', label: 'Segunda', selected: false },
    { value: 'tuesday', label: 'Terça', selected: false },
    { value: 'wednesday', label: 'Quarta', selected: false },
    { value: 'thursday', label: 'Quinta', selected: false },
    { value: 'friday', label: 'Sexta', selected: false },
    { value: 'saturday', label: 'Sábado', selected: false }
  ];

  newExcludedDate: string = '';

  // Prévia da Agenda
  previewDays: PreviewDay[] = [];
  selectedPreviewDay: PreviewDay | null = null;
  showModal = false;
  previewMode: 'monthly' | 'weekly' = 'monthly';
  weeklyDays: PreviewDay[] = [];
  weeklyStartDate: Date = new Date();

  columns: { titulo: string, label: string }[] = [
    { titulo: 'Nome', label: 'name' },
    { titulo: 'Criado Por', label: 'createdBy' },
    { titulo: 'Agendados', label: 'scheduledCount' }
  ];

  tableActions = [
    { label: 'Visualizar', icon: 'fas fa-eye', action: 'view', class: 'btn-view' },
    { label: 'Excluir', icon: 'fas fa-trash', action: 'delete', class: 'btn-delete' }
  ];

  constructor(
    private router: Router,
    private authService: AuthService,
    private schedulesService: SchedulesService,
    private groupsService: GroupsService
  ) { }

  ngOnInit(): void {
    this.loadGroups();
    this.loadAgendas();
  }

  loadGroups(): void {
    this.groupsService.getAllGroups().subscribe({
      next: (groups) => {
        this.groups = groups.map(group => ({
          id: group.id,
          name: group.name
        }));
      },
      error: (error) => {
        console.error('Erro ao carregar grupos:', error);
      }
    });
  }

  loadAgendas(): void {
    this.loading = true;
    this.error = null;

    this.schedulesService.getAll().subscribe({
      next: (schedules) => {
        this.agendas = schedules.map((schedule: any) => ({
          id: schedule.Id || schedule.id,
          name: schedule.Name || schedule.name,
          // Usa o valor vindo da API para "Criado Por"
          createdBy: schedule.CreatedBy || schedule.createdBy,
          scheduledCount: 0 // TODO: Calcular contagem de agendamentos quando disponível
        }));
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.error = 'Erro ao carregar agendas';
        console.error('Erro ao carregar agendas:', error);
      }
    });
  }

  refreshAgendas(): void {
    this.loadAgendas();
  }

  startCreating(): void {
    this.isCreating = true;
    this.currentStep = 1;
    this.resetForm();
  }

  resetForm(): void {
    this.formData = {
      name: '',
      description: '',
      groupId: null,
      canExternalLink: false,
      allDays: true,
      excludeHolidays: false,
      excludeSpecificDates: false,
      excludedDates: [],
      commercialHours: true,
      includePresenceStatus: false,
      intervalDuration: '30',
      attendancesPerSlot: 1,
      timeSlots: []
    };

    this.newExcludedDate = '';
    this.weekDays.forEach(day => day.selected = false);
  }

  cancelForm(): void {
    this.isCreating = false;
    this.editingAgendaId = null;
    this.isEditing = false;
    this.currentStep = 1;
    this.resetForm();
  }

  nextStep(): void {
    if (this.currentStep < 3) {
      this.currentStep++;
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  goToStep(step: number): void {
    if (step >= 1 && step <= 4) {
      this.currentStep = step;
      if (step === 4) {
        this.updatePreviewForMode();
      }
    }
  }

  addTimeSlot(): void {
    this.formData.timeSlots.push({ start: '', end: '' });
  }

  removeTimeSlot(index: number): void {
    this.formData.timeSlots.splice(index, 1);
  }

  calculateEndTime(index: number): void {
    const slot = this.formData.timeSlots[index];

    if (!slot.start) {
      slot.end = '';
      return;
    }

    // Pega a duração do intervalo em minutos
    const duration = parseInt(this.formData.intervalDuration);

    // Converte o horário de início para minutos
    const [hours, minutes] = slot.start.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes;

    // Adiciona a duração
    const newTotalMinutes = totalMinutes + duration;

    // Converte de volta para horas e minutos
    const newHours = Math.floor(newTotalMinutes / 60);
    const newMinutes = newTotalMinutes % 60;

    // Se passar de 24 horas, limita a 23:59
    if (newHours >= 24) {
      slot.end = '23:59';
    } else {
      // Formata para HH:MM
      slot.end = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;
    }
  }

  recalculateAllEndTimes(): void {
    // Recalcula todos os horários finais quando a duração do intervalo muda
    this.formData.timeSlots.forEach((slot, index) => {
      if (slot.start) {
        this.calculateEndTime(index);
      }
    });
  }

  addExcludedDate(): void {
    if (this.newExcludedDate && !this.formData.excludedDates.includes(this.newExcludedDate)) {
      this.formData.excludedDates.push(this.newExcludedDate);
      this.formData.excludedDates.sort(); // Ordenar as datas
      this.newExcludedDate = '';
    }
  }

  removeExcludedDate(index: number): void {
    this.formData.excludedDates.splice(index, 1);
  }

  saveAgenda(): void {
    if (!this.formData.groupId) {
      this.error = 'Selecione um grupo para a agenda';
      return;
    }

    this.isSaving = true;

    // Preparar dados da agenda no formato da API
    const scheduleData: Schedule = {
      Name: this.formData.name,
      Description: this.formData.description,
      GroupId: this.formData.groupId,
      Sunday: this.formData.allDays || this.weekDays.find(d => d.value === 'sunday')?.selected || false,
      Monday: this.formData.allDays || this.weekDays.find(d => d.value === 'monday')?.selected || false,
      Tuesday: this.formData.allDays || this.weekDays.find(d => d.value === 'tuesday')?.selected || false,
      Wednesday: this.formData.allDays || this.weekDays.find(d => d.value === 'wednesday')?.selected || false,
      Thursday: this.formData.allDays || this.weekDays.find(d => d.value === 'thursday')?.selected || false,
      Friday: this.formData.allDays || this.weekDays.find(d => d.value === 'friday')?.selected || false,
      Saturday: this.formData.allDays || this.weekDays.find(d => d.value === 'saturday')?.selected || false,
      AppointmentsByHours: this.formData.attendancesPerSlot,
      TimeIntervalMinutes: parseInt(this.formData.intervalDuration),
      CanExternalLink: !!this.formData.canExternalLink,
      CreatedBy: this.authService.getUserNameFromToken() ||
        ''
    };

    const saveOperation = this.isEditing && this.editingAgendaId
      ? this.schedulesService.update(this.editingAgendaId, scheduleData)
      : this.schedulesService.create(scheduleData);

    saveOperation.subscribe({
      next: (savedSchedule: any) => {
        const scheduleId = savedSchedule.Id || savedSchedule.id;

        // Processar datas excluídas e time slots
        if (scheduleId) {
          if (this.isEditing) {
            // Ao editar, limpar dados antigos antes de criar novos
            this.clearOldScheduleData(scheduleId).then(() => {
              this.saveScheduleRelatedData(scheduleId);
            }).catch(error => {
              console.error('Erro ao limpar dados antigos:', error);
              this.saveScheduleRelatedData(scheduleId);
            });
          } else {
            // Nova agenda, apenas salvar os dados
            this.saveScheduleRelatedData(scheduleId);
          }
        } else {
          this.isSaving = false;
          this.cancelForm();
          this.loadAgendas();
        }
      },
      error: (error) => {
        this.isSaving = false;
        this.error = 'Erro ao salvar agenda';
        console.error('Erro ao salvar agenda:', error);
      }
    });
  }

  clearOldScheduleData(scheduleId: number): Promise<void> {
    return new Promise((resolve, reject) => {
      const promises: Promise<any>[] = [];

      // Buscar e deletar excluded days antigos
      this.schedulesService.getAllExcludedDays().subscribe({
        next: (excludedDays: any[]) => {
          const oldDays = excludedDays.filter((day: any) =>
            (day.ScheduleId || day.scheduleId) === scheduleId
          );

          oldDays.forEach((day: any) => {
            const dayId = day.Id || day.id;
            if (dayId) {
              promises.push(this.schedulesService.deleteExcludedDay(dayId).toPromise());
            }
          });

          // Buscar e deletar time slots antigos
          this.schedulesService.getAllTimeSlots().subscribe({
            next: (timeSlots: any[]) => {
              const oldSlots = timeSlots.filter((slot: any) =>
                (slot.ScheduleId || slot.scheduleId) === scheduleId
              );

              oldSlots.forEach((slot: any) => {
                const slotId = slot.Id || slot.id;
                if (slotId) {
                  promises.push(this.schedulesService.deleteTimeSlot(slotId).toPromise());
                }
              });

              Promise.all(promises)
                .then(() => {
                  resolve();
                })
                .catch(error => {
                  console.error('❌ Erro ao limpar dados antigos:', error);
                  reject(error);
                });
            },
            error: (error) => {
              console.error('Erro ao buscar time slots antigos:', error);
              reject(error);
            }
          });
        },
        error: (error) => {
          console.error('Erro ao buscar excluded days antigos:', error);
          reject(error);
        }
      });
    });
  }

  saveScheduleRelatedData(scheduleId: number): void {
    const promises: Promise<any>[] = [];

    // 1. Salvar datas excluídas
    if (this.formData.excludeSpecificDates && this.formData.excludedDates.length > 0) {
      const excludedPromises = this.formData.excludedDates.map(dateStr => {
        // Garante que a data está no formato correto (YYYY-MM-DD)
        const date = new Date(dateStr + 'T00:00:00');
        const excludedDay: ExcludedDay = {
          ScheduleId: scheduleId,
          Date: date.toISOString()
        };
        return this.schedulesService.createExcludedDay(excludedDay).toPromise();
      });
      promises.push(...excludedPromises);
    } else {
    }

    // Executar todas as promessas
    Promise.all(promises).then(() => {
      this.isSaving = false;
      this.cancelForm();
      this.loadAgendas();
    }).catch(error => {
      console.error('❌ Erro ao salvar configurações:', error);
      this.isSaving = false;
      this.error = 'Agenda salva, mas houve erro ao configurar horários';
      this.loadAgendas();
    });
  }

  onRowClick(agenda: any): void {
    this.isEditing = true;
    this.currentStep = 1;
    this.loadAgendaForEdit(agenda.id);
  }

  loadAgendaForEdit(scheduleId: number): void {
    this.loading = true;
    this.editingAgendaId = scheduleId;

    this.schedulesService.getById(scheduleId).subscribe({
      next: (schedule: any) => {
        // Preencher o formulário com os dados da agenda
        this.formData.name = schedule.Name || schedule.name;
        this.formData.description = schedule.Description || schedule.description;
        this.formData.groupId = schedule.GroupId || schedule.groupId || null;
        this.formData.canExternalLink = schedule.CanExternalLink ?? schedule.canExternalLink ?? false;
        this.formData.attendancesPerSlot = schedule.AppointmentsByHours || schedule.appointmentsByHours;
        this.formData.intervalDuration = (schedule.TimeIntervalMinutes || schedule.timeIntervalMinutes).toString();

        // Configurar dias da semana
        const sunday = schedule.Sunday ?? schedule.sunday;
        const monday = schedule.Monday ?? schedule.monday;
        const tuesday = schedule.Tuesday ?? schedule.tuesday;
        const wednesday = schedule.Wednesday ?? schedule.wednesday;
        const thursday = schedule.Thursday ?? schedule.thursday;
        const friday = schedule.Friday ?? schedule.friday;
        const saturday = schedule.Saturday ?? schedule.saturday;

        const allDaysSelected = sunday && monday && tuesday && wednesday && thursday && friday && saturday;
        this.formData.allDays = allDaysSelected;

        if (!allDaysSelected) {
          this.weekDays.find(d => d.value === 'sunday')!.selected = sunday;
          this.weekDays.find(d => d.value === 'monday')!.selected = monday;
          this.weekDays.find(d => d.value === 'tuesday')!.selected = tuesday;
          this.weekDays.find(d => d.value === 'wednesday')!.selected = wednesday;
          this.weekDays.find(d => d.value === 'thursday')!.selected = thursday;
          this.weekDays.find(d => d.value === 'friday')!.selected = friday;
          this.weekDays.find(d => d.value === 'saturday')!.selected = saturday;
        }

        // Carregar datas excluídas
        this.loadExcludedDatesForSchedule(scheduleId);

        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.error = 'Erro ao carregar agenda para edição';
        console.error('Erro ao carregar agenda:', error);
        this.cancelForm();
      }
    });
  }

  loadExcludedDatesForSchedule(scheduleId: number): void {
    this.schedulesService.getAllExcludedDays().subscribe({
      next: (excludedDays: any[]) => {
        const scheduleDays = excludedDays.filter((day: any) =>
          (day.ScheduleId || day.scheduleId) === scheduleId
        );
        if (scheduleDays.length > 0) {
          this.formData.excludeSpecificDates = true;
          this.formData.excludedDates = scheduleDays.map((day: any) =>
            new Date(day.Date || day.date).toISOString().split('T')[0]
          );
        }
      },
      error: (error) => {
        console.error('Erro ao carregar datas excluídas:', error);
      }
    });
  }

  onSearchChange(searchTerm: string): void {
    // A filtragem é feita automaticamente pelo componente app-table
  }

  onActionClick(event: { action: string, item: any }): void {

    if (event.action === 'delete') {
      this.confirmDelete(event.item);
    } else if (event.action === 'view') {
      this.onRowClick(event.item);
    }
  }

  confirmDelete(agenda: any): void {

    if (confirm(`Tem certeza que deseja excluir a agenda "${agenda.name}"?\n\nEsta ação não pode ser desfeita.`)) {
      this.deleteAgenda(agenda.id);
    }
  }

  deleteAgenda(scheduleId: number): void {
    this.loading = true;
    this.error = null;

    this.schedulesService.delete(scheduleId).subscribe({
      next: () => {
        this.loading = false;
        this.loadAgendas();
        // Sucesso silencioso ou pode adicionar uma notificação
      },
      error: (error) => {
        console.error('❌ Erro ao excluir agenda:', error);
        this.loading = false;
        this.error = 'Erro ao excluir agenda';
        console.error('Erro ao excluir agenda:', error);
      }
    });
  }

  onPreviewModeChange(): void {
    this.updatePreviewForMode();
  }

  updatePreviewForMode(): void {
    if (this.previewMode === 'monthly') {
      this.generatePreview();
    } else {
      this.generateWeeklyPreview();
    }
  }

  getHolidays(): string[] {
    // Lista de feriados nacionais brasileiros de 2025/2026 (exemplo)
    return [
      '2025-01-01', '2025-02-24', '2025-02-25', '2025-04-18', '2025-04-21',
      '2025-05-01', '2025-06-19', '2025-09-07', '2025-10-12', '2025-11-02',
      '2025-11-15', '2025-11-20', '2025-12-25',
      '2026-01-01', '2026-02-16', '2026-02-17', '2026-04-03', '2026-04-21',
      '2026-05-01', '2026-06-04', '2026-09-07', '2026-10-12', '2026-11-02',
      '2026-11-15', '2026-11-20', '2026-12-25'
    ];
  }

  createPreviewDay(date: Date): PreviewDay {
    const dateString = date.toISOString().split('T')[0];
    const dayOfWeek = date.getDay();
    const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
    const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

    let available = true;
    let reason = '';

    const holidays = this.getHolidays();

    // Verificar se é feriado
    if (this.formData.excludeHolidays && holidays.includes(dateString)) {
      available = false;
      reason = 'holiday';
    }

    // Verificar se está na lista de datas excluídas
    if (this.formData.excludeSpecificDates && this.formData.excludedDates.includes(dateString)) {
      available = false;
      reason = 'excluded';
    }

    // Verificar se o dia da semana está configurado
    if (!this.formData.allDays && available) {
      const weekDayMap = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayValue = weekDayMap[dayOfWeek];
      const isDaySelected = this.weekDays.find(d => d.value === dayValue)?.selected;

      if (!isDaySelected) {
        available = false;
        reason = 'weekday';
      }
    }

    // Gerar slots de horário
    let timeSlots: TimeSlot[] = [];

    if (available) {
      if (this.formData.commercialHours) {
        // Horário comercial padrão: 08:00 - 18:00
        timeSlots = this.generateTimeSlotsForRange('08:00', '18:00');
      } else {
        // Usar horários personalizados
        this.formData.timeSlots.forEach(slot => {
          if (slot.start && slot.end) {
            timeSlots = [...timeSlots, ...this.generateTimeSlotsForRange(slot.start, slot.end)];
          }
        });
      }
    }

    return {
      fullDate: date,
      dayName: dayNames[dayOfWeek],
      dayNumber: date.getDate(),
      monthName: monthNames[date.getMonth()],
      available,
      reason,
      timeSlots
    };
  }

  generatePreview(): void {
    this.previewDays = [];
    const today = new Date();

    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      this.previewDays.push(this.createPreviewDay(date));
    }
  }

  generateWeeklyPreview(): void {
    this.weeklyDays = [];
    // Normalizar para início na segunda-feira
    const start = new Date(this.weeklyStartDate);
    const day = start.getDay(); // 0=Dom, 1=Seg, ...
    const mondayOffset = (day + 6) % 7;
    start.setDate(start.getDate() - mondayOffset);

    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      this.weeklyDays.push(this.createPreviewDay(date));
    }
  }

  prevWeek(): void {
    this.weeklyStartDate = new Date(this.weeklyStartDate);
    this.weeklyStartDate.setDate(this.weeklyStartDate.getDate() - 7);
    this.generateWeeklyPreview();
  }

  nextWeek(): void {
    this.weeklyStartDate = new Date(this.weeklyStartDate);
    this.weeklyStartDate.setDate(this.weeklyStartDate.getDate() + 7);
    this.generateWeeklyPreview();
  }

  generateTimeSlotsForRange(startTime: string, endTime: string): TimeSlot[] {
    const slots: TimeSlot[] = [];
    const duration = parseInt(this.formData.intervalDuration);

    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    let currentMinutes = startHour * 60 + startMin;
    const endMinutes = endHour * 60 + endMin;

    while (currentMinutes < endMinutes) {
      const slotStart = `${String(Math.floor(currentMinutes / 60)).padStart(2, '0')}:${String(currentMinutes % 60).padStart(2, '0')}`;
      currentMinutes += duration;
      const slotEnd = `${String(Math.floor(currentMinutes / 60)).padStart(2, '0')}:${String(currentMinutes % 60).padStart(2, '0')}`;

      if (currentMinutes <= endMinutes) {
        slots.push({ start: slotStart, end: slotEnd, capacity: this.formData.attendancesPerSlot });
      }
    }

    return slots;
  }

  selectPreviewDay(day: PreviewDay): void {
    this.selectedPreviewDay = day;
    this.showModal = true;
  }

  closeModal(): void {
    this.showModal = false;
  }
}
